import React, { useState, useMemo } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
  Legend,
  Area,
} from "recharts";
import { Slider } from "../ui/slider";
import { Sun, TrendDown, TrendUp, Trophy, Calendar } from "@phosphor-icons/react";

/**
 * InteractiveSavingsSlider - drag through years 1-25 to see cumulative
 * "do nothing" vs "go solar" costs, with break-even point highlighted.
 */
export default function InteractiveSavingsSlider({ yearlyProjection, paybackYears, netCost }) {
  const data = yearlyProjection || [];
  const maxYear = data.length || 25;
  const [year, setYear] = useState(Math.max(1, paybackYears || 7));

  const point = useMemo(() => data.find((d) => d.year === year) || data[0], [data, year]);
  const breakEven = useMemo(() => data.find((d) => d.year === paybackYears) || null, [data, paybackYears]);

  const noSolarCum = point?.no_solar_cumulative || 0;
  const withSolarCum = point?.with_solar_cumulative || 0;
  const savingsAtYear = noSolarCum - withSolarCum;
  const isPostBreakEven = year >= (paybackYears || 99);

  return (
    <div
      data-testid="results-slider"
      className="rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-8 md:p-10 text-white relative overflow-hidden mb-8"
    >
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-3xl" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <div className="inline-flex items-center gap-2 bg-amber-400/15 border border-amber-400/30 rounded-full px-3 py-1">
            <Calendar size={14} weight="fill" className="text-amber-300" />
            <span className="text-xs font-bold uppercase tracking-wider text-amber-200">
              Interactive 25-year outlook
            </span>
          </div>
        </div>
        <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight text-white">
          Drag to see what doing nothing actually costs.
        </h3>
        <p className="text-slate-300 mt-2 max-w-3xl">
          HECO rates climb ~4.5% every year. Solar locks today's price. Slide through the
          years to see when you break even — and how the gap keeps widening after that.
        </p>

        {/* Live stat tiles */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="bg-red-500/10 backdrop-blur-sm border border-red-500/30 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <TrendUp size={16} weight="bold" className="text-red-300" />
              <div className="text-[11px] uppercase tracking-wider text-red-200 font-bold">
                Stay with HECO — Year {year}
              </div>
            </div>
            <div className="font-display text-3xl md:text-4xl font-bold text-white">
              ${noSolarCum.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="text-[11px] text-red-200/80 mt-1">cumulative payments to utility</div>
          </div>

          <div className="bg-emerald-500/10 backdrop-blur-sm border border-emerald-500/30 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <Sun size={16} weight="fill" className="text-emerald-300" />
              <div className="text-[11px] uppercase tracking-wider text-emerald-200 font-bold">
                Go Solar — Year {year}
              </div>
            </div>
            <div className="font-display text-3xl md:text-4xl font-bold text-white">
              ${withSolarCum.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="text-[11px] text-emerald-200/80 mt-1">
              all-in (system + remaining grid usage)
            </div>
          </div>

          <div
            className={`backdrop-blur-sm border rounded-2xl p-5 transition-colors ${
              isPostBreakEven
                ? "bg-amber-500/15 border-amber-400/40"
                : "bg-white/5 border-white/10"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              {isPostBreakEven ? (
                <Trophy size={16} weight="fill" className="text-amber-300" />
              ) : (
                <TrendDown size={16} weight="bold" className="text-slate-300" />
              )}
              <div className="text-[11px] uppercase tracking-wider font-bold text-amber-200">
                {isPostBreakEven ? "You're ahead by" : "Investment offset so far"}
              </div>
            </div>
            <div
              className={`font-display text-3xl md:text-4xl font-bold ${
                savingsAtYear >= 0 ? "text-amber-300" : "text-slate-300"
              }`}
            >
              ${Math.abs(savingsAtYear).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              {savingsAtYear < 0 && (
                <span className="text-base font-medium text-slate-400 ml-1">to recover</span>
              )}
            </div>
            <div className="text-[11px] text-amber-200/80 mt-1">
              {isPostBreakEven ? "and still climbing" : `break-even ~ year ${paybackYears}`}
            </div>
          </div>
        </div>

        {/* Slider */}
        <div className="mt-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between text-sm mb-4">
            <span className="text-slate-300 font-semibold">Year</span>
            <span
              data-testid="slider-year-display"
              className="font-display text-2xl font-bold text-white"
            >
              {year}
              <span className="text-base text-slate-400 font-medium"> / {maxYear}</span>
            </span>
          </div>
          <Slider
            data-testid="results-year-slider"
            value={[year]}
            min={1}
            max={maxYear}
            step={1}
            onValueChange={(v) => setYear(v[0])}
            className="my-4"
          />
          <div className="flex items-center justify-between text-[11px] text-slate-400 mt-2">
            <span>Year 1 — install year</span>
            {paybackYears && (
              <span className="text-amber-300 font-semibold">⚡ Break-even @ year {paybackYears}</span>
            )}
            <span>Year {maxYear}</span>
          </div>
        </div>

        {/* Live chart with reference lines */}
        <div className="mt-6 bg-white rounded-2xl p-5">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data}>
              <defs>
                <linearGradient id="redGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EF4444" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="emeraldGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="year"
                stroke="#64748B"
                tick={{ fontSize: 11 }}
                label={{ value: "Year", position: "insideBottom", offset: -2, fontSize: 11, fill: "#94A3B8" }}
              />
              <YAxis
                stroke="#64748B"
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                formatter={(v) => `$${v.toLocaleString()}`}
                contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
                labelFormatter={(l) => `Year ${l}`}
              />
              <Area
                type="monotone"
                dataKey="no_solar_cumulative"
                stroke="#EF4444"
                strokeWidth={2.5}
                fill="url(#redGrad)"
                name="Stay with HECO"
              />
              <Area
                type="monotone"
                dataKey="with_solar_cumulative"
                stroke="#10B981"
                strokeWidth={2.5}
                fill="url(#emeraldGrad)"
                name="Go solar"
              />
              {/* Vertical line at slider year */}
              <ReferenceLine
                x={year}
                stroke="#0EA5E9"
                strokeWidth={2}
                strokeDasharray="4 4"
                label={{ value: `Yr ${year}`, position: "top", fontSize: 11, fill: "#0EA5E9", fontWeight: 700 }}
              />
              {/* Break-even marker */}
              {breakEven && (
                <ReferenceDot
                  x={breakEven.year}
                  y={breakEven.with_solar_cumulative}
                  r={7}
                  fill="#F59E0B"
                  stroke="#FFF"
                  strokeWidth={2}
                  label={{
                    value: "Break-even",
                    position: "top",
                    fontSize: 10,
                    fill: "#F59E0B",
                    fontWeight: 700,
                  }}
                />
              )}
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
